// Shared redemption CREATE journey (plan §12): amount → method/destination → review →
// confirm, on the new server contract (quote + create). Works on desktop and mobile
// (responsive, no modal dependency). On-system colors: green/neutral/red, surface tokens.
import React, { useEffect, useMemo, useState } from "react";
import { payoutsApi, fmtMoney, minorFromInput } from "@/lib/payouts";

function apiMsg(e) {
  const d = e?.response?.data?.detail;
  if (d && typeof d === "object") return d.message || d.code || "Request failed";
  return d || e?.message || "Request failed";
}

function newIdemKey() {
  try { return crypto.randomUUID(); } catch { return `idem-${Date.now()}-${Math.round(Math.random() * 1e9)}`; }
}

const METHOD_LABELS = { cashapp: "Cash App", venmo: "Venmo", paypal: "PayPal", zelle: "Zelle", ach: "Bank (ACH)" };
function methodLabel(m) { return METHOD_LABELS[m] || (m || "ACH").toUpperCase(); }

export default function RedeemFlow({ options, onDone, onClose }) {
  const [dests, setDests] = useState([]);
  const [destId, setDestId] = useState(null);
  const [amountStr, setAmountStr] = useState("");
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [done, setDone] = useState(null);
  // connect-a-handle (manual P2P rail) sub-form
  const [showConnect, setShowConnect] = useState(false);
  const [connectMethod, setConnectMethod] = useState("");
  const [connectHandle, setConnectHandle] = useState("");
  const [connectErr, setConnectErr] = useState(null);
  const [connectBusy, setConnectBusy] = useState(false);

  const loadDests = (preferId) =>
    payoutsApi.destinations().then((d) => {
      const list = Array.isArray(d) ? d : [];
      setDests(list);
      const def = (preferId && list.find((x) => x.id === preferId))
        || list.find((x) => x.verified) || list[0];
      if (def) setDestId(def.id);
      return list;
    }).catch(() => setDests([]));

  useEffect(() => { loadDests(); }, []);

  // Manual P2P methods the server advertises (capability-driven; empty when the rail is off).
  const manualMethods = (options?.methods || []).filter((m) => m.rail === "manual");

  const addHandle = async () => {
    setConnectErr(null); setConnectBusy(true);
    try {
      const created = await payoutsApi.addManualDestination(connectMethod, connectHandle.trim());
      await loadDests(created.id);
      setShowConnect(false); setConnectHandle(""); setConnectMethod("");
    } catch (e) { setConnectErr(apiMsg(e)); } finally { setConnectBusy(false); }
  };

  const minMinor = options?.limits?.min?.minor ?? 5000;
  const maxMinor = options?.limits?.max_per_request?.minor ?? 100000;
  const availMinor = options?.balances?.available?.minor ?? 0;
  const amountMinor = minorFromInput(amountStr);

  const amountError = useMemo(() => {
    if (!amountStr) return null;
    if (amountMinor < minMinor) return `Minimum is ${fmtMoney({ minor: minMinor })}`;
    if (amountMinor > maxMinor) return `Maximum is ${fmtMoney({ minor: maxMinor })}`;
    if (amountMinor > availMinor) return `More than your available ${fmtMoney({ minor: availMinor })}`;
    return null;
  }, [amountStr, amountMinor, minMinor, maxMinor, availMinor]);

  const canQuote = amountMinor >= minMinor && !amountError && destId;

  const getQuote = async () => {
    setErr(null); setBusy(true);
    try {
      // rail follows the chosen destination (manual P2P or ACH) — never hardcoded.
      const rail = (dests.find((d) => d.id === destId)?.rail) || "ach";
      const q = await payoutsApi.quote({ amount_minor: amountMinor, currency: "USD",
        destination_id: destId, rail });
      setQuote(q);
    } catch (e) { setErr(apiMsg(e)); } finally { setBusy(false); }
  };

  const unmet = (quote?.requirements || []).filter((r) => r.status !== "satisfied");

  const confirm = async () => {
    setErr(null); setBusy(true);
    try {
      const r = await payoutsApi.create({ quote_id: quote.quote_id, quote_version: quote.version,
        idempotency_key: newIdemKey() });
      setDone(r);
      onDone && onDone();
    } catch (e) { setErr(apiMsg(e)); } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="rounded-[14px] border border-[#1F1F23] bg-[#0E0E11] p-4">
        <div className="flex items-center gap-2 text-[14px] font-semibold" style={{ color: "#00D632" }}>
          <span>✓</span> Redemption requested
        </div>
        <div className="mt-1 font-mono text-[12px] text-[#71717a]">{done.reference}</div>
        <div className="mt-2 text-[12px] text-[#a1a1aa]">
          Approval and bank delivery are separate steps — you can track this below.
        </div>
        <button className="mt-3 text-[13px] font-semibold" style={{ color: "#00D632" }}
          onClick={() => { setDone(null); setQuote(null); setAmountStr(""); onClose && onClose(); }}>
          Done
        </button>
      </div>
    );
  }

  return (
    <div className="rounded-[14px] border border-[#1F1F23] bg-[#0E0E11] p-4">
      <div className="mb-3 flex items-center justify-between">
        <div className="text-[14px] font-semibold text-[#e4e4e7]">Request a redemption</div>
        {onClose && (
          <button className="text-[12px] text-[#a1a1aa]" onClick={onClose}>Close</button>
        )}
      </div>

      {!quote ? (
        <div className="flex flex-col gap-3">
          <label className="text-[12px] text-[#71717a]">
            Amount (available {fmtMoney({ minor: availMinor })})
            <div className="mt-1 flex items-center gap-2">
              <span className="text-[16px] text-[#a1a1aa]">$</span>
              <input
                inputMode="decimal"
                className="w-full rounded-[10px] border border-[#1F1F23] bg-[#141417] px-3 py-2 text-[18px] font-semibold text-white outline-none"
                placeholder="0.00"
                value={amountStr}
                onChange={(e) => setAmountStr(e.target.value.replace(/[^0-9.]/g, ""))}
                aria-label="Redemption amount"
              />
            </div>
          </label>
          <div className="flex gap-2">
            {[minMinor, 10000, Math.min(maxMinor, availMinor)].map((m, i) => (
              <button key={i}
                className="rounded-full border border-[#1F1F23] bg-[#141417] px-3 py-1 text-[12px] text-[#a1a1aa]"
                onClick={() => setAmountStr((m / 100).toFixed(2))}>
                {i === 2 ? "Max" : fmtMoney({ minor: m })}
              </button>
            ))}
          </div>

          <label className="text-[12px] text-[#71717a]">
            Payout account
            <select
              className="mt-1 w-full rounded-[10px] border border-[#1F1F23] bg-[#141417] px-3 py-2 text-[14px] text-white outline-none"
              value={destId || ""}
              onChange={(e) => setDestId(e.target.value)}
              aria-label="Payout account">
              {dests.length === 0 && <option value="">No payout account yet</option>}
              {dests.map((d) => (
                <option key={d.id} value={d.id}>
                  {methodLabel(d.method)} {d.last_four || ""} {d.verified ? "✓" : "Pending review"}
                </option>
              ))}
            </select>
          </label>

          {manualMethods.length > 0 && (
            showConnect ? (
              <div className="rounded-[10px] border border-[#1F1F23] bg-[#141417] p-3">
                <div className="mb-2 text-[12px] font-semibold text-[#e4e4e7]">Connect a payout app</div>
                <select
                  className="mb-2 w-full rounded-[10px] border border-[#1F1F23] bg-[#0E0E11] px-3 py-2 text-[14px] text-white outline-none"
                  value={connectMethod}
                  onChange={(e) => setConnectMethod(e.target.value)}
                  aria-label="Payout app">
                  <option value="">Choose an app…</option>
                  {manualMethods.map((m) => (
                    <option key={m.method} value={m.method}>{m.label}</option>
                  ))}
                </select>
                <input
                  className="mb-2 w-full rounded-[10px] border border-[#1F1F23] bg-[#0E0E11] px-3 py-2 text-[14px] text-white outline-none"
                  placeholder={manualMethods.find((m) => m.method === connectMethod)?.handle_hint || "handle"}
                  value={connectHandle}
                  onChange={(e) => setConnectHandle(e.target.value)}
                  aria-label="Payout handle"
                />
                {connectErr && <div className="mb-2 text-[12px]" style={{ color: "#EF4444" }}>{connectErr}</div>}
                <div className="text-[11px] text-[#71717a] mb-2">
                  We verify ownership before your first payout — make sure this handle is yours.
                </div>
                <div className="flex gap-2">
                  <button className="rounded-[10px] border border-[#1F1F23] px-3 py-1.5 text-[12px] text-[#a1a1aa]"
                    onClick={() => { setShowConnect(false); setConnectErr(null); }}>Cancel</button>
                  <button
                    disabled={!connectMethod || !connectHandle.trim() || connectBusy}
                    onClick={addHandle}
                    className="rounded-[10px] px-3 py-1.5 text-[12px] font-bold text-[#0A0A0A] disabled:opacity-50"
                    style={{ backgroundColor: "#00D632" }}>
                    {connectBusy ? "…" : "Connect"}
                  </button>
                </div>
              </div>
            ) : (
              <button className="self-start text-[12px] font-semibold" style={{ color: "#00D632" }}
                onClick={() => setShowConnect(true)}>
                + Connect Cash App, Venmo, PayPal or Zelle
              </button>
            )
          )}

          {amountError && <div className="text-[12px]" style={{ color: "#EF4444" }}>{amountError}</div>}
          {err && <div className="text-[12px]" style={{ color: "#EF4444" }}>{err}</div>}

          <button
            disabled={!canQuote || busy}
            onClick={getQuote}
            className="rounded-[10px] px-4 py-2.5 text-[14px] font-bold text-[#0A0A0A] disabled:opacity-50"
            style={{ backgroundColor: "#00D632" }}>
            {busy ? "…" : "Review"}
          </button>
        </div>
      ) : (
        <div className="flex flex-col gap-2 text-[13px]">
          <Row label="Amount redeemed" value={fmtMoney(quote.amount)} />
          <Row label="Fee" value={fmtMoney(quote.fee)} />
          <Row label="Net cash prize" value={fmtMoney(quote.net)} strong />
          <div className="mt-1 text-[12px] text-[#71717a]">
            Review target {options?.review_target_business_days ?? 2} business days · bank delivery is separate.
          </div>
          {unmet.length > 0 && (
            <ul className="mt-1 text-[12px]" style={{ color: "#EF4444" }}>
              {unmet.map((r, i) => <li key={i}>{r.message || r.code}</li>)}
            </ul>
          )}
          {err && <div className="text-[12px]" style={{ color: "#EF4444" }}>{err}</div>}
          <div className="mt-2 flex gap-2">
            <button className="rounded-[10px] border border-[#1F1F23] px-4 py-2 text-[13px] text-[#a1a1aa]"
              onClick={() => { setQuote(null); setErr(null); }}>Back</button>
            <button
              disabled={busy || unmet.length > 0}
              onClick={confirm}
              className="flex-1 rounded-[10px] px-4 py-2 text-[14px] font-bold text-[#0A0A0A] disabled:opacity-50"
              style={{ backgroundColor: "#00D632" }}>
              {busy ? "…" : "Confirm redemption"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value, strong }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-[#71717a]">{label}</span>
      <span className={strong ? "font-display font-bold text-white" : "text-[#e4e4e7]"}>{value}</span>
    </div>
  );
}
